// src/components/ui/ProfessionalThemeProvider.js
// Theme provider component for consistent professional styling

import React, { createContext, useContext } from 'react';
import UIDesignSystemEngine from '../../analytics/UIDesignSystemEngine';

const ThemeContext = createContext();

export const useProfessionalTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useProfessionalTheme must be used within a ProfessionalThemeProvider');
  }
  return context;
};

export const ProfessionalThemeProvider = ({ children }) => {
  const designSystem = new UIDesignSystemEngine();
  const theme = designSystem.generateTheme();
  
  // Inject CSS custom properties into document
  React.useEffect(() => {
    const cssVars = designSystem.generateCSSCustomProperties();
    const root = document.documentElement;
    
    Object.entries(cssVars).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Cleanup on unmount
    return () => {
      Object.keys(cssVars).forEach(property => {
        root.style.removeProperty(property);
      });
    };
  }, []);

  const value = {
    theme,
    designSystem,
    // Helper functions for components
    getComponentStyles: (type, variant, state) => 
      designSystem.generateComponentStyles(type, variant, state),
    getTailwindClasses: (type, variant) => 
      designSystem.generateTailwindClasses(type, variant),
    getPerformanceColor: (value, benchmark, isInverse) => 
      designSystem.getPerformanceColor(value, benchmark, isInverse),
    getChartColors: () => designSystem.getChartColorPalette(),
    generateGrid: (columns) => designSystem.generateResponsiveGrid(columns)
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ProfessionalThemeProvider;
